import type { FlightDetailsType } from "@/core/types/flight";

type TravelerLike = Record<string, unknown> & {
  public_id?: string;
  title?: string;
  first_name?: string;
  last_name?: string;
  gender?: string;
  dob?: string;
  date_of_birth?: string;
  passenger_type?: string;
  nationality?: string;
  nic?: string | null;
  cnic?: string | null;
  passport_no?: string | null;
  passport_number?: string | null;
  passport_expiry_date?: string | null;
  passport_expiry?: string | null;
  is_international?: boolean;
  additional_data?: unknown;
};

type FlightDetailsWithTravelers = FlightDetailsType & {
  travelers?: TravelerLike[];
};

const mapTravelerToPassenger = (
  traveler: TravelerLike,
  isInternational: boolean,
) => {
  const dob = traveler.dob ?? traveler.date_of_birth ?? "";
  return {
    ...traveler,
    public_id: traveler.public_id,
    title: traveler.title,
    first_name: traveler.first_name,
    last_name: traveler.last_name,
    gender: traveler.gender,
    dob,
    date_of_birth: dob,
    passenger_type: traveler.passenger_type,
    nationality: traveler.nationality,
    cnic: traveler.cnic ?? traveler.nic ?? null,
    passport_number: traveler.passport_number ?? traveler.passport_no ?? null,
    passport_expiry:
      traveler.passport_expiry ?? traveler.passport_expiry_date ?? null,
    passport_expiry_date: traveler.passport_expiry_date ?? "",
    is_international: traveler.is_international ?? Boolean(isInternational),
    additional_data: traveler.additional_data,
  };
};

/**
 * Ensures AddOns always read `passengers`, whether API/store used
 * legacy `passengers` or new `travelers`.
 */
export const normalizeFlightDetailsPassengers = (
  flightDetails: FlightDetailsType | null | undefined,
): FlightDetailsType | null => {
  if (!flightDetails) return null;

  const details = flightDetails as FlightDetailsWithTravelers;
  const existingPassengers = Array.isArray(details.passengers)
    ? details.passengers
    : [];

  if (existingPassengers.length > 0) {
    return details;
  }

  const travelers = Array.isArray(details.travelers) ? details.travelers : [];
  if (travelers.length === 0) {
    return details;
  }

  return {
    ...details,
    passengers: travelers.map((traveler) =>
      mapTravelerToPassenger(traveler, Boolean(details.is_international)),
    ),
  };
};

export const getFlightDetailsPassengers = (
  flightDetails: FlightDetailsType | null | undefined,
): any[] => {
  return normalizeFlightDetailsPassengers(flightDetails)?.passengers ?? [];
};
